import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  api,
  Video,
  VideoChatCitation,
  VideoChatHistory,
  VideoChatMessage,
  VideoChatSseEvent,
  VideoScene,
  VideoSpeaker,
  VideoTimeline,
  VideoTimelineCategory,
  VideoTimelineEvent,
  VideoTranscriptSegment,
  videoChatStream,
} from "../lib/api";
/**
 * Interactive video viewer: native HTML5 player + clickable scene strip
 * + click-to-seek transcript pane.
 *
 * Sync model: the source of truth for "what time is showing" is the
 * <video> element's currentTime (via the timeupdate event). The scene
 * strip and transcript both compute their active row from that value
 * by binary search — no setInterval polling needed.
 *
 * Auth: the video's `play_url` already carries a signed token, so the
 * <video src> attribute can hit it directly. We never funnel the
 * source bytes through fetch() — that would force the browser to
 * buffer the whole file into a Blob URL and Range-seeks would stop
 * working.
 */

const BASE = (import.meta as any).env?.VITE_API_BASE ?? "/api";

interface Props {
  caseId: number;
  video: Video;
  /** Called when the user opens the viewer for activity logging
   *  purposes. Optional; the parent typically only cares once. */
  onView?: () => void;
}

type RightTab = "transcript" | "timeline" | "favorites";

export default function VideoPlayer({ caseId, video, onView }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const transcriptRef = useRef<HTMLDivElement>(null);
  const [scenes, setScenes] = useState<VideoScene[] | null>(null);
  const [transcript, setTranscript] = useState<VideoTranscriptSegment[] | null>(null);
  const [speakers, setSpeakers] = useState<VideoSpeaker[] | null>(null);
  const [timeline, setTimeline] = useState<VideoTimeline | null>(null);
  const [activeTab, setActiveTab] = useState<RightTab>("transcript");
  const [regenerating, setRegenerating] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [hasViewed, setHasViewed] = useState(false);
  const [popOut, setPopOut] = useState(false);
  const [askOpen, setAskOpen] = useState(false);
  const modalTranscriptRef = useRef<HTMLDivElement>(null);

  // Load scenes + transcript + speakers whenever the video id changes.
  // Promise.all so all three requests run in parallel and the pane is
  // interactive as soon as they've settled.
  useEffect(() => {
    let cancelled = false;
    setScenes(null);
    setTranscript(null);
    setSpeakers(null);
    setErr(null);
    (async () => {
      try {
        const [sc, tr, sp] = await Promise.all([
          api.get<VideoScene[]>(`/cases/${caseId}/videos/${video.id}/scenes`),
          api.get<VideoTranscriptSegment[]>(
            `/cases/${caseId}/videos/${video.id}/transcript`,
          ),
          api.get<VideoSpeaker[]>(`/cases/${caseId}/videos/${video.id}/speakers`),
        ]);
        if (cancelled) return;
        setScenes(sc);
        setTranscript(tr);
        setSpeakers(sp);
      } catch (e: any) {
        if (!cancelled) setErr(e?.message ?? "Failed to load video details");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [caseId, video.id]);

  // Timeline fetch: load on mount, and refetch any time the parent's
  // polling surfaces a new status / event count / generated-at. That
  // way the user doesn't have to click "Regenerate" or even switch tabs
  // to see freshly-extracted events appear.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const tl = await api.get<VideoTimeline>(
          `/cases/${caseId}/videos/${video.id}/timeline`,
        );
        if (!cancelled) setTimeline(tl);
      } catch (e: any) {
        if (!cancelled) {
          // A timeline-fetch failure shouldn't blow up the whole player,
          // since the transcript/scenes pane is the user's main path.
          // Surface it inside the timeline tab instead.
          setTimeline({
            video_id: video.id,
            status: "failed",
            error: e?.message ?? "Failed to load timeline",
            items: [],
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    caseId,
    video.id,
    video.timeline_status,
    video.timeline_event_count,
    video.timeline_generated_at,
  ]);

  const regenerateTimeline = useCallback(async () => {
    if (regenerating) return;
    setRegenerating(true);
    try {
      await api.post(
        `/cases/${caseId}/videos/${video.id}/timeline/regenerate`,
      );
      // Optimistically flip local state so the banner shows immediately;
      // the parent's poll will keep things in sync from here.
      setTimeline((prev) =>
        prev ? { ...prev, status: "queued", error: null } : prev,
      );
    } catch (e: any) {
      setErr(e?.message ?? "Failed to regenerate timeline");
    } finally {
      setRegenerating(false);
    }
  }, [caseId, video.id, regenerating]);

  // Optimistically flip is_favorite locally and POST/DELETE on the server.
  // On failure, revert. The Favorites tab is just a filtered view over the
  // same `timeline.items` array, so a single state update keeps both tabs
  // in sync without a separate refetch.
  const toggleFavorite = useCallback(
    async (eventId: number) => {
      let newValue = false;
      setTimeline((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          items: prev.items.map((it) => {
            if (it.id !== eventId) return it;
            newValue = !it.is_favorite;
            return { ...it, is_favorite: newValue };
          }),
        };
      });
      try {
        const path = `/cases/${caseId}/videos/${video.id}/timeline-events/${eventId}/favorite`;
        if (newValue) {
          await api.post(path);
        } else {
          await api.del(path);
        }
      } catch (e: any) {
        // Revert.
        setTimeline((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            items: prev.items.map((it) =>
              it.id === eventId ? { ...it, is_favorite: !newValue } : it,
            ),
          };
        });
        setErr(e?.message ?? "Failed to update favorite");
      }
    },
    [caseId, video.id],
  );

  const onTimeUpdate = useCallback(() => {
    const t = videoRef.current?.currentTime ?? 0;
    setCurrentTime(t);
    if (!hasViewed && t > 0) {
      setHasViewed(true);
      onView?.();
    }
  }, [hasViewed, onView]);

  const seek = useCallback((seconds: number) => {
    const el = videoRef.current;
    if (!el) return;
    el.currentTime = Math.max(0, seconds);
    void el.play().catch(() => {
      /* autoplay may be blocked; user can press play manually */
    });
  }, []);

  // Index of the scene whose [start, end) contains currentTime. -1 when
  // we're past the last scene's end (rare — typically duration === last
  // scene end).
  const activeSceneIdx = useMemo(() => {
    if (!scenes || scenes.length === 0) return -1;
    return findSpan(scenes, currentTime, (s) => s.start_seconds, (s) => s.end_seconds);
  }, [scenes, currentTime]);

  const activeSegmentIdx = useMemo(() => {
    if (!transcript || transcript.length === 0) return -1;
    return findSpan(transcript, currentTime, (s) => s.start_seconds, (s) => s.end_seconds);
  }, [transcript, currentTime]);

  // Auto-scroll the transcript list so the active row stays in view,
  // but ONLY when the user isn't actively reading something further
  // down (i.e. only scroll when the new active row falls outside the
  // visible region, not on every timeupdate tick).
  useEffect(() => {
    if (activeSegmentIdx < 0) return;
    const container = transcriptRef.current;
    if (!container) return;
    const row = container.querySelector<HTMLDivElement>(
      `[data-seg-idx="${activeSegmentIdx}"]`,
    );
    if (!row) return;
    const cRect = container.getBoundingClientRect();
    const rRect = row.getBoundingClientRect();
    if (rRect.top < cRect.top || rRect.bottom > cRect.bottom) {
      row.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [activeSegmentIdx]);

  if (err) return <div className="alert alert--error">{err}</div>;

  // The signed token in play_url has the case_id baked into the path
  // (matching the API), so we can use it as-is across BASE.
  const playSrc = video.play_url ? `${BASE}${video.play_url.replace(/^\/api/, "")}` : null;

  // Combined view: raw spk_<n> token -> human label. Uses the
  // user-supplied display_name if there is one, else falls back to
  // "Speaker 1 / Speaker 2 / ..." numbered in appearance order so
  // the SPA never exposes internal token names.
  const speakerNames = useMemo(
    () => resolveSpeakerNames(transcript ?? [], speakers ?? []),
    [transcript, speakers],
  );

  // List of speakers known for this video (segments-derived + minted
  // overrides), in stable appearance/append order. Passed to the
  // segment-level picker so the user always sees the same dropdown.
  const speakerOrder = useMemo(() => {
    const out: string[] = [];
    for (const seg of transcript ?? []) {
      const k = seg.speaker_label ?? "";
      if (k && !out.includes(k)) out.push(k);
    }
    for (const sp of speakers ?? []) {
      if (!out.includes(sp.speaker_label)) out.push(sp.speaker_label);
    }
    return out;
  }, [transcript, speakers]);

  const segmentCountByLabel = useMemo(() => {
    const m = new Map<string, number>();
    for (const seg of transcript ?? []) {
      const k = seg.speaker_label ?? "";
      if (!k) continue;
      m.set(k, (m.get(k) ?? 0) + 1);
    }
    return m;
  }, [transcript]);

  const favoriteCount = useMemo(
    () => (timeline?.items ?? []).filter((it) => it.is_favorite).length,
    [timeline],
  );

  const reassignSegment = useCallback(
    async (segmentId: number, newLabel: string) => {
      try {
        const updated = await api.patch<VideoTranscriptSegment>(
          `/cases/${caseId}/videos/${video.id}/transcript/${segmentId}`,
          { speaker_label: newLabel },
        );
        setTranscript((prev) =>
          prev
            ? prev.map((s) =>
                s.id === segmentId
                  ? {
                      ...s,
                      speaker_label: updated.speaker_label,
                      speaker_display_name: updated.speaker_display_name,
                    }
                  : s,
              )
            : prev,
        );
        // The backend may have minted a fresh `video_speakers` row when
        // the user picks "+ New speaker"; pull the up-to-date list so
        // the picker rows + counts stay accurate.
        try {
          const sp = await api.get<VideoSpeaker[]>(
            `/cases/${caseId}/videos/${video.id}/speakers`,
          );
          setSpeakers(sp);
        } catch {
          // Non-fatal: the local mutation above is enough for the
          // immediate render; the next mount will reconcile.
        }
      } catch (e: any) {
        setErr(e?.message ?? "Failed to reassign segment");
      }
    },
    [caseId, video.id],
  );

  const renameSpeaker = useCallback(
    async (label: string, name: string | null) => {
      try {
        const cleaned = (name ?? "").trim();
        const updated = await api.put<VideoSpeaker>(
          `/cases/${caseId}/videos/${video.id}/speakers/${encodeURIComponent(label)}`,
          { display_name: cleaned ? cleaned : null },
        );
        setSpeakers((prev) => {
          if (!prev) return [updated];
          const i = prev.findIndex((p) => p.speaker_label === label);
          if (i >= 0) {
            const copy = prev.slice();
            copy[i] = { ...copy[i], display_name: updated.display_name };
            return copy;
          }
          return [...prev, updated];
        });
        setTranscript((prev) =>
          prev
            ? prev.map((s) =>
                s.speaker_label === label
                  ? { ...s, speaker_display_name: updated.display_name }
                  : s,
              )
            : prev,
        );
      } catch (e: any) {
        setErr(e?.message ?? "Failed to rename speaker");
      }
    },
    [caseId, video.id],
  );

  const mintSpeakerLabel = useCallback((): string => {
    // Smallest non-negative integer not already used by a segment or
    // an override row. Keeps labels dense so they read naturally as
    // "Speaker 1, Speaker 2, Speaker 3, ..."
    const used = new Set<string>();
    for (const seg of transcript ?? []) if (seg.speaker_label) used.add(seg.speaker_label);
    for (const sp of speakers ?? []) used.add(sp.speaker_label);
    let n = 0;
    while (used.has(`spk_${n}`)) n += 1;
    return `spk_${n}`;
  }, [transcript, speakers]);

  return (
    <div className="video-player">
      <div className="video-player__left">
        <div className="video-player__frame">
          {playSrc ? (
            <video
              ref={videoRef}
              src={playSrc}
              controls
              preload="metadata"
              onTimeUpdate={onTimeUpdate}
              onLoadedMetadata={onTimeUpdate}
              className="video-player__video"
            />
          ) : (
            <div className="video-player__placeholder">
              {video.status === "uploading"
                ? "Upload still in progress…"
                : video.status === "failed"
                  ? video.error_message ?? "Processing failed"
                  : "Video is being prepared…"}
            </div>
          )}
        </div>
        <div className="video-player__meta">
          <div className="video-player__meta-title">
            {video.original_name}
            {video.translated && (
              <span
                className="doc-tag doc-tag--translated"
                title={
                  video.source_language
                    ? `Transcript translated to English from ${video.source_language} by AI during processing. The original video is unchanged.`
                    : "Transcript translated to English by AI during processing. The original video is unchanged."
                }
              >
                AI Translated{video.source_language ? ` · from ${video.source_language}` : ""}
              </span>
            )}
          </div>
          <div className="video-player__meta-sub">
            {formatDuration(video.duration_seconds)} ·{" "}
            {video.width && video.height
              ? `${video.width}×${video.height}`
              : "—"}{" "}
            — {scenes?.length ?? 0} scenes — {transcript?.length ?? 0} segments
          </div>
        </div>

        <SceneStrip
          scenes={scenes}
          activeIdx={activeSceneIdx}
          onPick={(s) => seek(s.start_seconds)}
        />
      </div>

      <div className="video-player__right">
        <button
          type="button"
          className="video-player__ask-btn"
          onClick={() => setAskOpen(true)}
          disabled={video.status !== "ready"}
          title={
            video.status !== "ready"
              ? "Video is still processing…"
              : "Ask questions about this video — answers are grounded in its transcript, timeline, and scenes"
          }
        >
          <AskTabIcon />
          <span>Ask AI about this video</span>
          <span className="video-player__ask-btn-hint">
            chat with history · downloadable
          </span>
        </button>

        <div className="video-player__tabs-row">
          <div
            className="video-player__tabs"
            role="tablist"
            aria-label="Transcript, timeline, and favorites"
          >
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === "transcript"}
              className={`video-player__tab${activeTab === "transcript" ? " video-player__tab--active" : ""}`}
              onClick={() => setActiveTab("transcript")}
            >
              Transcript
              {transcript && transcript.length > 0 && (
                <span className="video-player__tab-count">
                  {transcript.length}
                </span>
              )}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === "timeline"}
              className={`video-player__tab${activeTab === "timeline" ? " video-player__tab--active" : ""}`}
              onClick={() => setActiveTab("timeline")}
            >
              Timeline
              {timeline && timeline.items.length > 0 && (
                <span className="video-player__tab-count">
                  {timeline.items.length}
                </span>
              )}
              {timeline &&
                (timeline.status === "queued" || timeline.status === "running") && (
                  <span
                    className="video-player__tab-dot"
                    aria-label="Generating"
                    title="AI is generating events…"
                  />
                )}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === "favorites"}
              className={`video-player__tab video-player__tab--fav${activeTab === "favorites" ? " video-player__tab--active" : ""}`}
              onClick={() => setActiveTab("favorites")}
              title="Your starred timeline events for this video"
            >
              <StarIcon filled={favoriteCount > 0} />
              Favorites
              {favoriteCount > 0 && (
                <span className="video-player__tab-count">{favoriteCount}</span>
              )}
            </button>
          </div>
          <button
            type="button"
            className="video-player__pop-btn"
            onClick={() => setPopOut(true)}
            title="Open in larger view"
            aria-label="Pop out transcript / timeline / favorites"
          >
            <PopOutIcon />
          </button>
        </div>

        {activeTab === "transcript" ? (
          <TranscriptPane
            transcript={transcript}
            activeSegmentIdx={activeSegmentIdx}
            speakerNames={speakerNames}
            speakerOrder={speakerOrder}
            segmentCountByLabel={segmentCountByLabel}
            seek={seek}
            transcriptRef={transcriptRef}
            transcribeProvider={video.transcribe_provider ?? null}
            onReassignSegment={reassignSegment}
            onRenameSpeaker={renameSpeaker}
            onMintSpeakerLabel={mintSpeakerLabel}
          />
        ) : activeTab === "timeline" ? (
          <TimelinePane
            timeline={timeline}
            currentTime={currentTime}
            seek={seek}
            onRegenerate={regenerateTimeline}
            regenerating={regenerating}
            videoReady={video.status === "ready"}
            hasGroundableData={
              (video.scene_count ?? 0) > 0 ||
              (video.transcript_segment_count ?? 0) > 0
            }
            onToggleFavorite={toggleFavorite}
          />
        ) : (
          <FavoritesPane
            timeline={timeline}
            currentTime={currentTime}
            seek={seek}
            onToggleFavorite={toggleFavorite}
            onJumpToTimeline={() => setActiveTab("timeline")}
          />
        )}
      </div>

      {popOut && (
        <PopOutModal
          caseId={caseId}
          video={video}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          transcript={transcript}
          activeSegmentIdx={activeSegmentIdx}
          speakerNames={speakerNames}
          speakerOrder={speakerOrder}
          segmentCountByLabel={segmentCountByLabel}
          seek={seek}
          transcriptRef={modalTranscriptRef}
          transcribeProvider={video.transcribe_provider ?? null}
          onReassignSegment={reassignSegment}
          onRenameSpeaker={renameSpeaker}
          onMintSpeakerLabel={mintSpeakerLabel}
          timeline={timeline}
          currentTime={currentTime}
          onRegenerate={regenerateTimeline}
          regenerating={regenerating}
          videoReady={video.status === "ready"}
          hasGroundableData={
            (video.scene_count ?? 0) > 0 ||
            (video.transcript_segment_count ?? 0) > 0
          }
          favoriteCount={favoriteCount}
          onToggleFavorite={toggleFavorite}
          onClose={() => setPopOut(false)}
          onOpenAsk={() => setAskOpen(true)}
        />
      )}

      {askOpen && (
        <VideoAskModal
          caseId={caseId}
          videoId={video.id}
          videoName={video.original_name}
          videoReady={video.status === "ready"}
          playSrc={playSrc}
          seek={(s) => {
            // "Open in main player" inside the citation popover uses this
            // — the modal closes so the user can see the player at full size.
            seek(s);
            setAskOpen(false);
          }}
          onClose={() => setAskOpen(false)}
        />
      )}
    </div>
  );
}

// --------------------------------------------------------------------------- //
//  Pop-out icon                                                               //
// --------------------------------------------------------------------------- //

function PopOutIcon() {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 13 13"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M8 1h4v4M12 1l-5 5M5 12H1V8M1 12l5-5" />
    </svg>
  );
}

function AskTabIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 16 16"
      fill="currentColor"
      aria-hidden="true"
      style={{ flexShrink: 0 }}
    >
      <path d="M8 1a7 7 0 1 0 0 14A7 7 0 0 0 8 1zm0 11.5a1 1 0 1 1 0-2 1 1 0 0 1 0 2zm.75-3.75a.75.75 0 0 1-1.5 0V8.5c0-.41.34-.75.75-.75h.25c.69 0 1.25-.56 1.25-1.25S8.94 5.25 8.25 5.25h-.5C7.07 5.25 6.5 5.81 6.5 6.5a.75.75 0 0 1-1.5 0C5 4.98 6.23 3.75 7.75 3.75h.5C9.77 3.75 11 4.98 11 6.5c0 1.38-.97 2.53-2.25 2.78v-.53z"/>
    </svg>
  );
}

function StarIcon({ filled }: { filled: boolean }) {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 16 16"
      fill={filled ? "currentColor" : "none"}
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ flexShrink: 0 }}
    >
      <path d="M8 1.8l1.96 3.96 4.37.64-3.16 3.08.75 4.36L8 11.78l-3.92 2.06.75-4.36L1.67 6.4l4.37-.64L8 1.8z" />
    </svg>
  );
}

function DownloadIcon() {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ flexShrink: 0 }}
    >
      <path d="M8 2v8m0 0L4.5 6.5M8 10l3.5-3.5M2.5 13.5h11" />
    </svg>
  );
}

// --------------------------------------------------------------------------- //
//  Pop-out modal                                                               //
// --------------------------------------------------------------------------- //

function PopOutModal({
  video,
  activeTab,
  onTabChange,
  transcript,
  activeSegmentIdx,
  speakerNames,
  speakerOrder,
  segmentCountByLabel,
  seek,
  transcriptRef,
  transcribeProvider,
  onReassignSegment,
  onRenameSpeaker,
  onMintSpeakerLabel,
  timeline,
  currentTime,
  onRegenerate,
  regenerating,
  videoReady,
  hasGroundableData,
  favoriteCount,
  onToggleFavorite,
  onClose,
  onOpenAsk,
}: {
  caseId: number;
  video: { id: number; original_name: string; status: string };
  activeTab: RightTab;
  onTabChange: (t: RightTab) => void;
  transcript: VideoTranscriptSegment[] | null;
  activeSegmentIdx: number;
  speakerNames: Map<string, string>;
  speakerOrder: string[];
  segmentCountByLabel: Map<string, number>;
  seek: (s: number) => void;
  transcriptRef: React.RefObject<HTMLDivElement>;
  transcribeProvider: string | null;
  onReassignSegment: (id: number, label: string) => Promise<void>;
  onRenameSpeaker: (label: string, name: string | null) => Promise<void>;
  onMintSpeakerLabel: () => string;
  timeline: VideoTimeline | null;
  currentTime: number;
  onRegenerate: () => void;
  regenerating: boolean;
  videoReady: boolean;
  hasGroundableData: boolean;
  favoriteCount: number;
  onToggleFavorite: (eventId: number) => Promise<void>;
  onClose: () => void;
  onOpenAsk: () => void;
}) {
  useEffect(() => {
    function onKey(ev: KeyboardEvent) {
      if (ev.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  return (
    <div
      className="modal no-print"
      role="dialog"
      aria-modal="true"
      aria-label={`${video.original_name} — ${activeTab}`}
      onClick={onClose}
    >
      <div
        className="modal__card modal__card--lg modal__card--split video-player__pop-modal"
        onClick={(ev) => ev.stopPropagation()}
      >
        <header className="modal__header">
          <div className="modal__header-main">
            <h2 className="modal__header-title">{video.original_name}</h2>
            <div
              className="video-player__tabs"
              role="tablist"
              aria-label="Transcript, timeline, and favorites"
            >
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === "transcript"}
                className={`video-player__tab${activeTab === "transcript" ? " video-player__tab--active" : ""}`}
                onClick={() => onTabChange("transcript")}
              >
                Transcript
                {transcript && transcript.length > 0 && (
                  <span className="video-player__tab-count">
                    {transcript.length}
                  </span>
                )}
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === "timeline"}
                className={`video-player__tab${activeTab === "timeline" ? " video-player__tab--active" : ""}`}
                onClick={() => onTabChange("timeline")}
              >
                Timeline
                {timeline && timeline.items.length > 0 && (
                  <span className="video-player__tab-count">
                    {timeline.items.length}
                  </span>
                )}
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === "favorites"}
                className={`video-player__tab video-player__tab--fav${activeTab === "favorites" ? " video-player__tab--active" : ""}`}
                onClick={() => onTabChange("favorites")}
                title="Your starred timeline events for this video"
              >
                <StarIcon filled={favoriteCount > 0} />
                Favorites
                {favoriteCount > 0 && (
                  <span className="video-player__tab-count">
                    {favoriteCount}
                  </span>
                )}
              </button>
            </div>
          </div>
          <button
            type="button"
            className="modal__close"
            onClick={onClose}
            aria-label="Close"
            title="Close (Esc)"
          >
            ×
          </button>
        </header>

        <div className="modal__body video-player__pop-modal-body">
          {activeTab === "transcript" ? (
            <TranscriptPane
              transcript={transcript}
              activeSegmentIdx={activeSegmentIdx}
              speakerNames={speakerNames}
              speakerOrder={speakerOrder}
              segmentCountByLabel={segmentCountByLabel}
              seek={seek}
              transcriptRef={transcriptRef}
              transcribeProvider={transcribeProvider}
              onReassignSegment={onReassignSegment}
              onRenameSpeaker={onRenameSpeaker}
              onMintSpeakerLabel={onMintSpeakerLabel}
            />
          ) : activeTab === "timeline" ? (
            <TimelinePane
              timeline={timeline}
              currentTime={currentTime}
              seek={seek}
              onRegenerate={onRegenerate}
              regenerating={regenerating}
              videoReady={videoReady}
              hasGroundableData={hasGroundableData}
              onToggleFavorite={onToggleFavorite}
            />
          ) : (
            <FavoritesPane
              timeline={timeline}
              currentTime={currentTime}
              seek={seek}
              onToggleFavorite={onToggleFavorite}
              onJumpToTimeline={() => onTabChange("timeline")}
            />
          )}
        </div>

        <footer className="modal__footer">
          <span className="muted modal__footer-hint">
            Click a row to seek the video · Esc to close
          </span>
          <div className="modal__footer-actions">
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => {
                onClose();
                onOpenAsk();
              }}
              disabled={!videoReady}
              title={
                !videoReady
                  ? "Video is still processing…"
                  : "Open the Ask AI chat for this video"
              }
            >
              Ask AI
            </button>
            <button type="button" className="btn" onClick={onClose}>
              Close
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}

// --------------------------------------------------------------------------- //
//  Video Ask pane                                                             //
// --------------------------------------------------------------------------- //

interface AskMessage {
  role: "user" | "assistant";
  content: string;
  citations?: VideoChatCitation[];
  /** true while a streaming response is still arriving */
  streaming?: boolean;
}

const KIND_LABEL: Record<string, string> = {
  segment: "Transcript",
  event: "Timeline",
  scene: "Scene",
};

function CitationList({
  citations,
  onPreview,
}: {
  citations: VideoChatCitation[];
  /** Open the centered, draggable mini-player popover for this citation. */
  onPreview: (c: VideoChatCitation) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? citations : citations.slice(0, 3);
  return (
    <div className="video-ask__citations">
      <div className="video-ask__citations-head">
        <span className="video-ask__citations-label">
          Sources ({citations.length})
        </span>
        {citations.length > 3 && (
          <button
            type="button"
            className="video-ask__citations-toggle"
            onClick={() => setExpanded((v) => !v)}
          >
            {expanded ? "Show less" : `+${citations.length - 3} more`}
          </button>
        )}
      </div>
      <ul className="video-ask__citations-list">
        {visible.map((c) => (
          <li key={c.token}>
            <button
              type="button"
              className={`video-ask__citation video-ask__citation--${c.kind}`}
              onClick={() => onPreview(c)}
              title={`Preview ${c.label}`}
            >
              <span className="video-ask__citation-kind">
                {KIND_LABEL[c.kind] ?? c.kind}
              </span>
              <span className="video-ask__citation-label">{c.label}</span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function VideoAskModal({
  caseId,
  videoId,
  videoName,
  videoReady,
  playSrc,
  seek,
  onClose,
}: {
  caseId: number;
  videoId: number;
  videoName: string;
  videoReady: boolean;
  /** Signed video URL — used by the inline citation preview popover so
   *  users can watch the cited moment without leaving the chat. */
  playSrc: string | null;
  /** "Open in main player" — closes this modal and seeks the underlying
   *  full-size player. The popover offers this as a secondary action; the
   *  default click-on-citation flow now stays in-chat. */
  seek: (s: number) => void;
  onClose: () => void;
}) {
  const [messages, setMessages] = useState<AskMessage[]>([]);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const logRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const downloadMenuRef = useRef<HTMLDivElement>(null);

  // Inline citation preview. Clicking a [VE3] / [VS42] / [VSC7] chip (or a
  // "Sources" row) opens a centered, draggable mini-player playing from the
  // cited moment, instead of closing the chat. Switching citations while
  // open just retargets the existing popover (preserving its dragged
  // position so users don't have to re-grab it after every click).
  const [previewCitation, setPreviewCitation] =
    useState<VideoChatCitation | null>(null);

  const openPreview = useCallback((citation: VideoChatCitation) => {
    setPreviewCitation(citation);
  }, []);

  const closePreview = useCallback(() => setPreviewCitation(null), []);

  // "Follow live" auto-scroll. While streaming, we normally pin the log to
  // the bottom so the user sees text arrive. If they manually scroll up,
  // we stop auto-following so they can read at their own pace, and surface
  // a "Jump to latest" pill to catch back up. We store the value in both
  // state (for the pill render) and a ref (so the scroll-on-message effect
  // doesn't need to depend on it and re-trigger).
  const [followLive, setFollowLive] = useState(true);
  const followLiveRef = useRef(true);

  // Esc to close + body scroll lock while the modal is open.
  useEffect(() => {
    function onKey(ev: KeyboardEvent) {
      if (ev.key !== "Escape") return;
      if (downloadOpen) {
        setDownloadOpen(false);
        return;
      }
      onClose();
    }
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose, downloadOpen]);

  // Click outside the download menu to dismiss.
  useEffect(() => {
    if (!downloadOpen) return;
    function onDocClick(ev: MouseEvent) {
      if (!downloadMenuRef.current) return;
      if (!downloadMenuRef.current.contains(ev.target as Node)) {
        setDownloadOpen(false);
      }
    }
    const t = setTimeout(() => {
      document.addEventListener("mousedown", onDocClick);
    }, 0);
    return () => {
      clearTimeout(t);
      document.removeEventListener("mousedown", onDocClick);
    };
  }, [downloadOpen]);

  // Load persisted history when the modal mounts. We do this each open so
  // a chat continued in another browser tab is reflected here too.
  useEffect(() => {
    let cancelled = false;
    setHistoryLoaded(false);
    (async () => {
      try {
        const data = await api.get<VideoChatHistory>(
          `/cases/${caseId}/videos/${videoId}/chat/history`,
        );
        if (cancelled) return;
        setMessages(
          (data.messages ?? []).map((m) => ({
            role: m.role,
            content: m.content,
            citations: m.citations,
          })),
        );
      } catch (e: any) {
        if (!cancelled) setErr(e?.message ?? "Failed to load chat history");
      } finally {
        if (!cancelled) setHistoryLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [caseId, videoId]);

  // Auto-scroll the log when messages change or text streams in — but
  // only while the user is "following live" (i.e. they haven't scrolled
  // up to read at their own pace). useLayoutEffect avoids a 1-frame
  // flash where the new chunk renders before we pin to bottom.
  useLayoutEffect(() => {
    const el = logRef.current;
    if (!el) return;
    if (!followLiveRef.current) return;
    el.scrollTop = el.scrollHeight;
  }, [messages]);

  // Track whether the user is parked near the bottom. We use a generous
  // threshold so trackpad inertia / sub-pixel rounding doesn't flip the
  // pill on/off.
  const onLogScroll = useCallback(() => {
    const el = logRef.current;
    if (!el) return;
    const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    const atBottom = distFromBottom <= 24;
    followLiveRef.current = atBottom;
    setFollowLive((prev) => (prev === atBottom ? prev : atBottom));
  }, []);

  // Re-pin to bottom and resume auto-follow.
  const jumpToLatest = useCallback(() => {
    const el = logRef.current;
    if (!el) return;
    followLiveRef.current = true;
    setFollowLive(true);
    el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }, []);

  // Focus the textarea once the modal is interactive.
  useEffect(() => {
    if (!historyLoaded) return;
    inputRef.current?.focus();
  }, [historyLoaded]);

  const submit = useCallback(async () => {
    const q = draft.trim();
    if (!q || busy) return;

    setDraft("");
    setErr(null);
    setBusy(true);
    // A fresh question is an explicit "I want to see what's happening"
    // intent — resume following the stream even if they'd scrolled up
    // while reading the previous answer.
    followLiveRef.current = true;
    setFollowLive(true);

    // Snapshot the history before adding the new user message.
    const history: VideoChatMessage[] = messages.map((m) => ({
      role: m.role,
      content: m.content,
    }));

    setMessages((prev) => [
      ...prev,
      { role: "user", content: q },
      { role: "assistant", content: "", streaming: true },
    ]);

    const ctrl = new AbortController();
    abortRef.current = ctrl;

    try {
      let accumulated = "";
      for await (const ev of videoChatStream(caseId, videoId, q, history, ctrl.signal)) {
        const e = ev as VideoChatSseEvent;
        if (e.type === "delta") {
          accumulated += e.text;
          setMessages((prev) => {
            const next = [...prev];
            next[next.length - 1] = { role: "assistant", content: accumulated, streaming: true };
            return next;
          });
        } else if (e.type === "done") {
          // Replace live-preview text with the server-scrubbed final text
          // (any hallucinated citation tokens are removed), and attach citations.
          setMessages((prev) => {
            const next = [...prev];
            next[next.length - 1] = {
              role: "assistant",
              content: e.text,
              citations: e.citations,
              streaming: false,
            };
            return next;
          });
          break;
        } else if (e.type === "error") {
          throw new Error(e.message);
        }
      }
    } catch (e: any) {
      if (e?.name === "AbortError") {
        setMessages((prev) => {
          const next = [...prev];
          if (next.length > 0) next[next.length - 1] = { ...next[next.length - 1], streaming: false };
          return next;
        });
      } else {
        setErr(e?.message ?? "Request failed");
        setMessages((prev) => {
          const next = [...prev];
          if (next.length > 0 && next[next.length - 1].role === "assistant" && !next[next.length - 1].content) {
            next.pop();
          }
          return next;
        });
      }
    } finally {
      setBusy(false);
      abortRef.current = null;
      inputRef.current?.focus();
    }
  }, [caseId, videoId, draft, busy, messages]);

  const onKeyDown = useCallback(
    (ev: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (ev.key === "Enter" && !ev.shiftKey) {
        ev.preventDefault();
        void submit();
      }
    },
    [submit],
  );

  const cancel = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  const clearHistory = useCallback(async () => {
    setConfirmClear(false);
    try {
      await api.del(`/cases/${caseId}/videos/${videoId}/chat/history`);
      setMessages([]);
      setErr(null);
      followLiveRef.current = true;
      setFollowLive(true);
    } catch (e: any) {
      setErr(e?.message ?? "Failed to clear chat history");
    }
  }, [caseId, videoId]);

  const downloadAs = useCallback(
    (fmt: "md" | "json") => {
      setDownloadOpen(false);
      const text = fmt === "json"
        ? buildChatJson(videoName, messages)
        : buildChatMarkdown(videoName, messages);
      const mime = fmt === "json" ? "application/json" : "text/markdown";
      const safeName = videoName.replace(/[^A-Za-z0-9._-]+/g, "_").slice(0, 80) || "video";
      const ts = new Date().toISOString().slice(0, 10);
      const filename = `${safeName}_chat_${ts}.${fmt}`;
      const blob = new Blob([text], { type: `${mime};charset=utf-8` });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // Defer revoke so Safari has time to start the download.
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    },
    [messages, videoName],
  );

  // Render assistant text as lightweight, streaming-safe markdown.
  //
  // We intentionally avoid pulling in a full markdown library — the model
  // only emits a small subset (headings, bold, italic, code, bullet/numbered
  // lists) and we want partial output mid-stream to render gracefully
  // (e.g. an unclosed "**foo" should appear as plain text, not eat the
  // rest of the buffer). Timestamps like [11:52] stay clickable, and raw
  // [VE##] / [VS##] / [VSC##] citation tokens that appear mid-stream are
  // styled as subtle chips so they don't shout at the user (the final
  // scrubbed text from the server replaces them once the stream completes).
  // Once the stream has finished and citations are attached, the chips
  // become clickable buttons that open an inline video preview popover.
  function renderAssistantBody(
    text: string,
    citations?: VideoChatCitation[],
  ): React.ReactNode {
    const lines = text.split("\n");
    const blocks: React.ReactNode[] = [];
    let i = 0;
    let key = 0;

    while (i < lines.length) {
      const line = lines[i];

      // Headings (#, ##, ###, ...). We squash everything to a couple of
      // visual levels because deep heading nesting doesn't help in a
      // chat-sized panel.
      const hMatch = line.match(/^(#{1,6})\s+(.*)$/);
      if (hMatch) {
        const level = hMatch[1].length;
        const cls =
          level <= 2
            ? "video-ask__md-h video-ask__md-h--1"
            : level === 3
            ? "video-ask__md-h video-ask__md-h--2"
            : "video-ask__md-h video-ask__md-h--3";
        blocks.push(
          <div key={`h-${key++}`} className={cls}>
            {renderInline(hMatch[2], `h-${key}`, citations)}
          </div>,
        );
        i++;
        continue;
      }

      // Unordered list (- foo / * foo).
      if (/^[-*]\s+/.test(line)) {
        const items: React.ReactNode[] = [];
        while (i < lines.length && /^[-*]\s+/.test(lines[i])) {
          const content = lines[i].replace(/^[-*]\s+/, "");
          items.push(
            <li key={`uli-${key++}`}>{renderInline(content, `uli-${key}`, citations)}</li>,
          );
          i++;
        }
        blocks.push(
          <ul key={`ul-${key++}`} className="video-ask__md-ul">
            {items}
          </ul>,
        );
        continue;
      }

      // Ordered list (1. foo / 2. bar). We preserve the starting number
      // so partial streams keep their numbering.
      const olMatch = line.match(/^(\d+)\.\s+/);
      if (olMatch) {
        const items: React.ReactNode[] = [];
        const start = parseInt(olMatch[1], 10);
        while (i < lines.length && /^\d+\.\s+/.test(lines[i])) {
          const content = lines[i].replace(/^\d+\.\s+/, "");
          items.push(
            <li key={`oli-${key++}`}>{renderInline(content, `oli-${key}`, citations)}</li>,
          );
          i++;
        }
        blocks.push(
          <ol key={`ol-${key++}`} className="video-ask__md-ol" start={start}>
            {items}
          </ol>,
        );
        continue;
      }

      // Blank line — paragraph separator.
      if (line.trim() === "") {
        i++;
        continue;
      }

      // Paragraph: greedily consume non-empty, non-block lines.
      const paraLines: string[] = [];
      while (
        i < lines.length &&
        lines[i].trim() !== "" &&
        !/^#{1,6}\s+/.test(lines[i]) &&
        !/^[-*]\s+/.test(lines[i]) &&
        !/^\d+\.\s+/.test(lines[i])
      ) {
        paraLines.push(lines[i]);
        i++;
      }
      const paraNodes: React.ReactNode[] = [];
      paraLines.forEach((pl, idx) => {
        if (idx > 0) paraNodes.push(<br key={`br-${key++}`} />);
        paraNodes.push(
          <span key={`pl-${key++}`}>{renderInline(pl, `p-${key}`, citations)}</span>,
        );
      });
      blocks.push(
        <p key={`p-${key++}`} className="video-ask__md-p">
          {paraNodes}
        </p>,
      );
    }

    return blocks;
  }

  // Inline pass: tokenize by timestamps (which need to become clickable
  // buttons that call seek()), then run markdown formatting on the rest.
  function renderInline(
    text: string,
    keyPrefix: string,
    citations?: VideoChatCitation[],
  ): React.ReactNode[] {
    const parts = text.split(/(\[\d+:\d{2}(?::\d{2})?\])/g);
    const out: React.ReactNode[] = [];
    parts.forEach((part, i) => {
      const tm = part.match(/^\[(\d+):(\d{2})(?::(\d{2}))?\]$/);
      if (tm) {
        const h = tm[3] !== undefined ? parseInt(tm[1], 10) : 0;
        const min = tm[3] !== undefined ? parseInt(tm[2], 10) : parseInt(tm[1], 10);
        const sec = tm[3] !== undefined ? parseInt(tm[3], 10) : parseInt(tm[2], 10);
        const total = h * 3600 + min * 60 + sec;
        out.push(
          <button
            key={`${keyPrefix}-ts-${i}`}
            type="button"
            className="video-ask__timestamp-link"
            onClick={() => seek(total)}
            title={`Jump to ${part.slice(1, -1)}`}
          >
            {part}
          </button>,
        );
      } else if (part) {
        out.push(...renderInlineMd(part, `${keyPrefix}-${i}`, citations));
      }
    });
    return out;
  }

  // Inline markdown: **bold**, `code`, [VE##] / [VS##] / [VSC##] citation
  // chips, then italics. Bold/code are handled first so their inner content
  // isn't double-parsed. When a citation is present in the message's
  // ``citations`` array, the chip becomes a clickable button that opens an
  // inline mini-player popover at that moment. While the answer is still
  // streaming (no citations attached yet), chips render as decorative
  // spans so partial output doesn't look broken.
  function renderInlineMd(
    text: string,
    keyPrefix: string,
    citations?: VideoChatCitation[],
  ): React.ReactNode[] {
    const out: React.ReactNode[] = [];
    const RE = /\*\*([^*\n][^*\n]*?)\*\*|`([^`\n]+)`|\[(VSC\d+|VS\d+|VE\d+)\]/g;
    let last = 0;
    let m: RegExpExecArray | null;
    let k = 0;
    while ((m = RE.exec(text)) !== null) {
      if (m.index > last) {
        out.push(...renderItalics(text.slice(last, m.index), `${keyPrefix}-t${k}`));
      }
      if (m[1] !== undefined) {
        out.push(
          <strong key={`${keyPrefix}-b${k}`}>
            {renderItalics(m[1], `${keyPrefix}-bi${k}`)}
          </strong>,
        );
      } else if (m[2] !== undefined) {
        out.push(
          <code key={`${keyPrefix}-c${k}`} className="video-ask__md-code">
            {m[2]}
          </code>,
        );
      } else if (m[3] !== undefined) {
        const tokName = m[3]; // e.g. "VE3", "VS42", "VSC7"
        const bracketed = `[${tokName}]`;
        const cite = citations?.find(
          (c) => c.token === bracketed || c.token === tokName,
        );
        const kindMod = cite ? `--${cite.kind}` : "";
        if (cite) {
          out.push(
            <button
              key={`${keyPrefix}-cite${k}`}
              type="button"
              className={`video-ask__md-citetoken video-ask__md-citetoken${kindMod} video-ask__md-citetoken--clickable`}
              onClick={() => openPreview(cite)}
              title={`${KIND_LABEL[cite.kind] ?? cite.kind} · ${cite.label} — click to preview`}
            >
              {tokName}
            </button>,
          );
        } else {
          // Streaming — citations not attached yet. Keep the original
          // decorative span so partial chips don't look like broken buttons.
          out.push(
            <span
              key={`${keyPrefix}-cite${k}`}
              className="video-ask__md-citetoken"
            >
              {tokName}
            </span>,
          );
        }
      }
      last = m.index + m[0].length;
      k++;
    }
    if (last < text.length) {
      out.push(...renderItalics(text.slice(last), `${keyPrefix}-tend`));
    }
    return out;
  }

  // Single-asterisk italics, with negative look-around so we don't try to
  // re-eat the bold markers (** ... **) or partial unclosed asterisks.
  function renderItalics(text: string, keyPrefix: string): React.ReactNode[] {
    const out: React.ReactNode[] = [];
    const RE = /(?<!\*)\*([^*\s][^*\n]*?)\*(?!\*)/g;
    let last = 0;
    let m: RegExpExecArray | null;
    let k = 0;
    while ((m = RE.exec(text)) !== null) {
      if (m.index > last) {
        out.push(
          <span key={`${keyPrefix}-t${k}`}>{text.slice(last, m.index)}</span>,
        );
      }
      out.push(<em key={`${keyPrefix}-i${k}`}>{m[1]}</em>);
      last = m.index + m[0].length;
      k++;
    }
    if (last < text.length) {
      out.push(<span key={`${keyPrefix}-tend`}>{text.slice(last)}</span>);
    }
    return out;
  }

  const hasMessages = messages.length > 0;

  return (
    <div
      className="modal no-print"
      role="dialog"
      aria-modal="true"
      aria-label={`Ask AI about ${videoName}`}
      onClick={(ev) => {
        // Only close when the user actually clicked the backdrop itself.
        // Without this guard, a drag inside the citation popover that
        // happens to release over the backdrop fires a synthetic click on
        // the .modal element (the deepest common ancestor of mousedown
        // and mouseup), which would otherwise dismiss the whole chat.
        if (ev.target === ev.currentTarget) onClose();
      }}
    >
      <div
        className="modal__card modal__card--lg modal__card--split video-ask-modal"
        onClick={(ev) => ev.stopPropagation()}
      >
        <header className="modal__header">
          <div className="modal__header-main">
            <h2 className="modal__header-title video-ask-modal__title">
              <AskTabIcon />
              Ask AI about this video
            </h2>
            <span className="muted video-ask-modal__subtitle">{videoName}</span>
          </div>
          <button
            type="button"
            className="modal__close"
            onClick={onClose}
            aria-label="Close"
            title="Close (Esc)"
          >
            ×
          </button>
        </header>

        <div className="modal__body video-ask-modal__body">
          <div className="video-ask video-ask--modal">
            <div className="video-ask__head video-ask-modal__head">
              <span className="muted video-ask__hint">
                Answers are grounded in this video's transcript, timeline,
                and scenes. Citations and timestamps jump the player to the
                cited moment.
              </span>
              <div className="video-ask-modal__head-actions">
                <div
                  className="video-ask-modal__menu-wrap"
                  ref={downloadMenuRef}
                >
                  <button
                    type="button"
                    className="btn btn--ghost btn--sm"
                    onClick={() => setDownloadOpen((v) => !v)}
                    disabled={!hasMessages}
                    title={
                      hasMessages
                        ? "Download this conversation"
                        : "Nothing to download yet"
                    }
                    aria-haspopup="menu"
                    aria-expanded={downloadOpen}
                  >
                    <DownloadIcon />
                    <span style={{ marginLeft: 6 }}>Download</span>
                  </button>
                  {downloadOpen && (
                    <div
                      className="video-ask-modal__menu"
                      role="menu"
                    >
                      <button
                        type="button"
                        className="video-ask-modal__menu-item"
                        onClick={() => downloadAs("md")}
                        role="menuitem"
                      >
                        Markdown (.md)
                      </button>
                      <button
                        type="button"
                        className="video-ask-modal__menu-item"
                        onClick={() => downloadAs("json")}
                        role="menuitem"
                      >
                        JSON (.json)
                      </button>
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  onClick={() => setConfirmClear(true)}
                  disabled={!hasMessages || busy}
                  title="Clear this conversation"
                >
                  Clear
                </button>
              </div>
            </div>

            <div className="video-ask__log-wrap">
            <div className="video-ask__log" ref={logRef} onScroll={onLogScroll}>
              {!historyLoaded && (
                <div className="muted" style={{ padding: 8 }}>
                  Loading conversation…
                </div>
              )}

              {historyLoaded && messages.length === 0 && (
                <div className="video-ask__empty">
                  <p className="muted">No questions yet. Try asking:</p>
                  <ul className="video-ask__suggestions">
                    <li>
                      <button
                        type="button"
                        className="video-ask__suggestion"
                        onClick={() => {
                          setDraft("What are the key events in this video?");
                          inputRef.current?.focus();
                        }}
                      >
                        What are the key events in this video?
                      </button>
                    </li>
                    <li>
                      <button
                        type="button"
                        className="video-ask__suggestion"
                        onClick={() => {
                          setDraft("What admissions or statements were made?");
                          inputRef.current?.focus();
                        }}
                      >
                        What admissions or statements were made?
                      </button>
                    </li>
                    <li>
                      <button
                        type="button"
                        className="video-ask__suggestion"
                        onClick={() => {
                          setDraft("Summarize what happened in this video.");
                          inputRef.current?.focus();
                        }}
                      >
                        Summarize what happened in this video.
                      </button>
                    </li>
                  </ul>
                </div>
              )}

              {messages.map((msg, i) => (
                <div
                  key={i}
                  className={`video-ask__msg video-ask__msg--${msg.role}${msg.streaming ? " video-ask__msg--streaming" : ""}`}
                >
                  <div className="video-ask__msg-label">
                    {msg.role === "user" ? "You" : "AI"}
                  </div>
                  <div
                    className={`video-ask__msg-body${msg.role === "assistant" ? " video-ask__msg-body--md" : ""}`}
                  >
                    {msg.role === "assistant"
                      ? renderAssistantBody(msg.content, msg.citations)
                      : msg.content}
                    {msg.streaming && <span className="video-ask__cursor" aria-hidden />}
                  </div>
                  {msg.role === "assistant" && !msg.streaming && msg.citations && msg.citations.length > 0 && (
                    <CitationList
                      citations={msg.citations}
                      onPreview={(c) => openPreview(c)}
                    />
                  )}
                </div>
              ))}

              {err && (
                <div className="alert alert--error video-ask__err">{err}</div>
              )}
            </div>
            {!followLive && (
              <button
                type="button"
                className="video-ask__jump-latest"
                onClick={jumpToLatest}
                aria-label="Jump to latest message"
                title="Jump to latest"
              >
                <span className="video-ask__jump-latest-arrow" aria-hidden>↓</span>
                <span>{busy ? "Jump to live" : "Jump to latest"}</span>
              </button>
            )}
            </div>

            <div className="video-ask__bar">
              <textarea
                ref={inputRef}
                className="video-ask__input"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder={
                  !videoReady
                    ? "Video is still processing…"
                    : "Ask a question about this video… (Enter to send, Shift+Enter for newline)"
                }
                disabled={!videoReady || busy}
                rows={2}
                aria-label="Ask a question about this video"
              />
              <div className="video-ask__bar-actions">
                {busy ? (
                  <button
                    type="button"
                    className="btn btn--ghost btn--sm"
                    onClick={cancel}
                    title="Stop generating"
                  >
                    Stop
                  </button>
                ) : (
                  <button
                    type="button"
                    className="btn btn--primary btn--sm"
                    onClick={() => void submit()}
                    disabled={!videoReady || !draft.trim()}
                  >
                    Ask Your Question
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        <footer className="modal__footer">
          <span className="muted modal__footer-hint">
            Conversation auto-saves · Esc to close
          </span>
        </footer>
      </div>

      {confirmClear && (
        <div
          className="modal no-print video-ask-modal__confirm-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Clear conversation"
          onClick={(ev) => ev.stopPropagation()}
        >
          <div
            className="modal__card video-ask-modal__confirm"
            onClick={(ev) => ev.stopPropagation()}
          >
            <h3 style={{ margin: 0 }}>Clear conversation?</h3>
            <p className="muted" style={{ margin: "8px 0 0" }}>
              This permanently deletes your chat history for this video.
            </p>
            <div className="modal__actions" style={{ marginTop: 14 }}>
              <button
                type="button"
                className="btn"
                onClick={() => setConfirmClear(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn--danger"
                onClick={() => void clearHistory()}
              >
                Clear
              </button>
            </div>
          </div>
        </div>
      )}

      {previewCitation && (
        <CitationPreviewPopover
          citation={previewCitation}
          caseId={caseId}
          videoId={videoId}
          playSrc={playSrc}
          onClose={closePreview}
          onOpenInMain={(s) => {
            closePreview();
            seek(s);
          }}
        />
      )}
    </div>
  );
}

// --------------------------------------------------------------------------- //
//  Citation preview popover                                                   //
// --------------------------------------------------------------------------- //
//
// A floating card with a <video> seeked to the citation's timestamp. Opens
// centered in the viewport and is freely draggable by its header so the
// user can park it wherever doesn't block the chat. Position survives
// switching citations (clicking another chip retargets the existing
// popover instead of resetting it). Dismisses on Esc, on click outside,
// or via the × button. The "Open in main player" footer button is the
// legacy seek-and-close-modal flow for when the user wants the full-size
// player.
const CITE_POPOVER_W = 480;
const CITE_POPOVER_H_EST = 320;
const CITE_POPOVER_MARGIN = 12;

/** Keep at least 80px of the popover on-screen so it can never get lost
 *  off the edge after dragging or after a viewport resize. */
function clampPos(
  x: number,
  y: number,
  vw: number,
  vh: number,
): { x: number; y: number } {
  const minVisible = 80;
  const minX = -(CITE_POPOVER_W - minVisible);
  const minY = 0;
  const maxX = vw - minVisible;
  const maxY = vh - minVisible;
  return {
    x: Math.min(maxX, Math.max(minX, x)),
    y: Math.min(maxY, Math.max(minY, y)),
  };
}

function CitationPreviewPopover({
  citation,
  caseId,
  videoId,
  playSrc,
  onClose,
  onOpenInMain,
}: {
  citation: VideoChatCitation;
  caseId: number;
  videoId: number;
  playSrc: string | null;
  onClose: () => void;
  onOpenInMain: (s: number) => void;
}) {
  const cardRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  // Clip-download UI: "Download" reveals a small confirm row with an
  // editable duration (seconds from the cited moment onward), then a
  // loader while the server stream-copies the sub-range into an MP4.
  const [clipOpen, setClipOpen] = useState(false);
  const [clipSeconds, setClipSeconds] = useState(60);
  const [clipBusy, setClipBusy] = useState(false);
  const [clipErr, setClipErr] = useState<string | null>(null);

  const downloadClip = useCallback(async () => {
    const secs = Math.min(600, Math.max(1, Math.round(clipSeconds || 0)));
    setClipBusy(true);
    setClipErr(null);
    try {
      const token = localStorage.getItem("cf_token");
      const url =
        `${BASE}/cases/${caseId}/videos/${videoId}/clip` +
        `?start=${encodeURIComponent(citation.at_seconds)}` +
        `&duration=${encodeURIComponent(secs)}`;
      const res = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) {
        throw new Error(`Export failed (${res.status})`);
      }
      const blob = await res.blob();
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objUrl;
      a.download = `clip_${Math.round(citation.at_seconds)}s.mp4`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objUrl);
      setClipOpen(false);
    } catch (e: any) {
      setClipErr(e?.message ?? "Export failed");
    } finally {
      setClipBusy(false);
    }
  }, [caseId, videoId, citation.at_seconds, clipSeconds]);

  // Initial position: centered on the viewport. We use a lazy initializer
  // so we only read window dimensions once on mount; subsequent renders
  // (e.g. citation switches) keep wherever the user dragged the card to.
  const [pos, setPos] = useState<{ x: number; y: number }>(() => {
    if (typeof window === "undefined") return { x: 0, y: 0 };
    const x = Math.max(
      CITE_POPOVER_MARGIN,
      Math.round((window.innerWidth - CITE_POPOVER_W) / 2),
    );
    const y = Math.max(
      CITE_POPOVER_MARGIN,
      Math.round((window.innerHeight - CITE_POPOVER_H_EST) / 2),
    );
    return { x, y };
  });

  const [dragging, setDragging] = useState(false);
  // We track the offset between the cursor and the card's top-left at
  // mousedown time so the card doesn't "jump" to align its corner with
  // the cursor when drag starts.
  const dragOffsetRef = useRef<{ dx: number; dy: number }>({ dx: 0, dy: 0 });

  const onHeaderMouseDown = useCallback((ev: React.MouseEvent) => {
    if (ev.button !== 0) return; // left button only
    const target = ev.target as Element;
    if (target.closest(".video-ask__cite-popover-close")) return;
    const card = cardRef.current;
    if (!card) return;
    const r = card.getBoundingClientRect();
    dragOffsetRef.current = { dx: ev.clientX - r.left, dy: ev.clientY - r.top };
    setDragging(true);
    ev.preventDefault();
  }, []);

  // Document-level move/up handlers active only while dragging. We attach
  // them at the document level so the user can drag with the cursor
  // outside the card without losing the drag.
  useEffect(() => {
    if (!dragging) return;
    const onMove = (ev: MouseEvent) => {
      const { dx, dy } = dragOffsetRef.current;
      const next = clampPos(
        ev.clientX - dx,
        ev.clientY - dy,
        window.innerWidth,
        window.innerHeight,
      );
      setPos(next);
    };
    const onUp = () => {
      setDragging(false);
      // Browsers fire a `click` on the closest common ancestor of the
      // mousedown and mouseup targets when those differ — for a drag
      // released outside this popover, that ancestor can be the modal
      // backdrop or even the document body, which would trigger their
      // click-to-close handlers. Swallow exactly one click (the synthetic
      // one tied to this drag-release) at the capture phase so it never
      // reaches ancestor handlers. The fallback timeout cleans up if no
      // click ever fires (e.g. user released on an iframe).
      const swallow = (e: MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        document.removeEventListener("click", swallow, true);
      };
      document.addEventListener("click", swallow, true);
      window.setTimeout(() => {
        document.removeEventListener("click", swallow, true);
      }, 50);
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
  }, [dragging]);

  // Re-clamp the position whenever the viewport resizes so the card
  // doesn't end up off-screen if the window shrinks.
  useEffect(() => {
    const onResize = () => {
      setPos((p) => clampPos(p.x, p.y, window.innerWidth, window.innerHeight));
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Seek to the cited moment as soon as metadata loads, and try autoplay.
  // Setting currentTime before `loadedmetadata` is silently ignored by
  // some browsers, so we wait for it (or fire immediately if already
  // ready, e.g. when switching citations on the same already-loaded src).
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const seekAndPlay = () => {
      try {
        v.currentTime = Math.max(0, citation.at_seconds);
      } catch {
        /* range error if duration unknown — ignore */
      }
      v.play().catch(() => {
        /* autoplay can be blocked; user clicks play */
      });
    };
    if (v.readyState >= 1) {
      seekAndPlay();
    } else {
      v.addEventListener("loadedmetadata", seekAndPlay, { once: true });
      return () => v.removeEventListener("loadedmetadata", seekAndPlay);
    }
  }, [citation.at_seconds]);

  // Esc to close. Click outside (and not on another citation trigger — so
  // the user can switch citations with a single click) also closes.
  useEffect(() => {
    function onKey(ev: KeyboardEvent) {
      if (ev.key === "Escape") {
        ev.stopPropagation();
        onClose();
      }
    }
    function onDown(ev: MouseEvent) {
      const t = ev.target as Element | null;
      if (!t) return;
      if (cardRef.current?.contains(t)) return;
      if (t.closest(".video-ask__md-citetoken--clickable")) return;
      if (t.closest(".video-ask__citation")) return;
      onClose();
    }
    document.addEventListener("keydown", onKey, true);
    document.addEventListener("mousedown", onDown, true);
    return () => {
      document.removeEventListener("keydown", onKey, true);
      document.removeEventListener("mousedown", onDown, true);
    };
  }, [onClose]);

  // Pause the embedded video when the popover unmounts so audio doesn't
  // keep playing in the background after dismissal.
  useEffect(() => {
    const v = videoRef.current;
    return () => {
      try {
        v?.pause();
      } catch {
        /* ignore */
      }
    };
  }, []);

  const kindLabel = KIND_LABEL[citation.kind] ?? citation.kind;
  const style: React.CSSProperties = {
    left: pos.x,
    top: pos.y,
    width: CITE_POPOVER_W,
  };

  return (
    <div
      ref={cardRef}
      className={[
        "video-ask__cite-popover",
        `video-ask__cite-popover--${citation.kind}`,
        dragging ? "video-ask__cite-popover--dragging" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      style={style}
      role="dialog"
      aria-modal="false"
      aria-label={`Preview of ${kindLabel} citation`}
      onClick={(ev) => ev.stopPropagation()}
    >
      <div
        className="video-ask__cite-popover-head"
        onMouseDown={onHeaderMouseDown}
        title="Drag to move"
      >
        <span className="video-ask__cite-popover-grip" aria-hidden>
          ⋮⋮
        </span>
        <span
          className={`video-ask__cite-popover-kind video-ask__cite-popover-kind--${citation.kind}`}
        >
          {kindLabel}
        </span>
        <span className="video-ask__cite-popover-title" title={citation.label}>
          {citation.label}
        </span>
        <button
          type="button"
          className="video-ask__cite-popover-close"
          onClick={onClose}
          onMouseDown={(ev) => ev.stopPropagation()}
          aria-label="Close preview"
          title="Close preview (Esc)"
        >
          ×
        </button>
      </div>
      <div className="video-ask__cite-popover-body">
        {playSrc ? (
          <video
            ref={videoRef}
            className="video-ask__cite-popover-video"
            src={playSrc}
            controls
            preload="metadata"
            playsInline
          />
        ) : (
          <div className="video-ask__cite-popover-empty muted">
            Video unavailable.
          </div>
        )}
      </div>
      {clipOpen ? (
        <div className="video-ask__cite-popover-foot video-ask__cite-popover-foot--clip">
          <div className="video-ask__clip-row">
            <span className="muted">
              Download from {formatClock(citation.at_seconds)} for
            </span>
            <input
              type="number"
              min={1}
              max={600}
              value={clipSeconds}
              disabled={clipBusy}
              onChange={(e) => setClipSeconds(Number(e.target.value))}
              className="video-ask__clip-input"
              aria-label="Clip length in seconds"
            />
            <span className="muted">seconds.</span>
          </div>
          <p className="muted video-ask__clip-note">
            The clip starts at the keyframe just before this moment so the
            opening isn't cut off.
          </p>
          {clipErr && <p className="video-ask__clip-err">{clipErr}</p>}
          <div className="video-ask__clip-actions">
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={() => {
                setClipOpen(false);
                setClipErr(null);
              }}
              disabled={clipBusy}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn--sm"
              onClick={() => void downloadClip()}
              disabled={clipBusy}
            >
              {clipBusy ? (
                <>
                  <span className="video-ask__clip-spinner" aria-hidden />
                  <span style={{ marginLeft: 6 }}>Preparing…</span>
                </>
              ) : (
                <>
                  <DownloadIcon />
                  <span style={{ marginLeft: 6 }}>Download clip</span>
                </>
              )}
            </button>
          </div>
        </div>
      ) : (
        <div className="video-ask__cite-popover-foot">
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={() => {
              setClipErr(null);
              setClipOpen(true);
            }}
            title="Download a short MP4 clip starting at this moment"
          >
            <DownloadIcon />
            <span style={{ marginLeft: 6 }}>Download</span>
          </button>
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={() => onOpenInMain(citation.at_seconds)}
            title="Close chat and jump the main player to this moment"
          >
            Open in main player →
          </button>
        </div>
      )}
    </div>
  );
}

function buildChatMarkdown(videoName: string, messages: AskMessage[]): string {
  const lines: string[] = [];
  lines.push(`# Video chat — ${videoName}`);
  lines.push("");
  lines.push(`_Exported ${new Date().toISOString()}_`);
  lines.push("");
  if (messages.length === 0) {
    lines.push("_(no messages)_");
    return lines.join("\n");
  }
  for (const m of messages) {
    lines.push(`## ${m.role === "user" ? "You" : "AI"}`);
    lines.push("");
    lines.push(m.content);
    lines.push("");
    if (m.role === "assistant" && m.citations && m.citations.length > 0) {
      lines.push("**Sources:**");
      for (const c of m.citations) {
        lines.push(`- [${c.kind}] ${c.label}`);
      }
      lines.push("");
    }
  }
  return lines.join("\n");
}

function buildChatJson(videoName: string, messages: AskMessage[]): string {
  return JSON.stringify(
    {
      video_name: videoName,
      exported_at: new Date().toISOString(),
      messages: messages.map((m) => ({
        role: m.role,
        content: m.content,
        citations: m.citations ?? [],
      })),
    },
    null,
    2,
  );
}

// --------------------------------------------------------------------------- //
//  Favorites pane                                                             //
// --------------------------------------------------------------------------- //

function FavoritesPane({
  timeline,
  currentTime,
  seek,
  onToggleFavorite,
  onJumpToTimeline,
}: {
  timeline: VideoTimeline | null;
  currentTime: number;
  seek: (s: number) => void;
  onToggleFavorite: (eventId: number) => Promise<void>;
  onJumpToTimeline: () => void;
}) {
  const favorites = useMemo(
    () => (timeline?.items ?? []).filter((it) => it.is_favorite),
    [timeline],
  );

  const activeIdx = useMemo(() => {
    if (favorites.length === 0) return -1;
    let best = -1;
    for (let i = 0; i < favorites.length; i += 1) {
      if (favorites[i].at_seconds <= currentTime) best = i;
      else break;
    }
    return best;
  }, [favorites, currentTime]);

  return (
    <>
      <div className="video-player__timeline-head">
        <span className="muted video-player__transcript-note">
          Your starred moments from the Timeline tab. Click a row to jump.
        </span>
      </div>
      {!timeline ? (
        <div className="muted">Loading timeline…</div>
      ) : favorites.length === 0 ? (
        <div className="video-player__fav-empty">
          <p className="muted" style={{ marginTop: 0 }}>
            No favorites yet.
          </p>
          <p className="muted" style={{ fontSize: 12 }}>
            Open the <strong>Timeline</strong> tab and tap the star on any
            event to bookmark it here.
          </p>
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={onJumpToTimeline}
          >
            Open Timeline
          </button>
        </div>
      ) : (
        <div className="video-player__timeline">
          {favorites.map((ev, i) => (
            <TimelineRow
              key={ev.id}
              event={ev}
              active={i === activeIdx}
              onPick={() => seek(ev.at_seconds)}
              onToggleFavorite={() => onToggleFavorite(ev.id)}
            />
          ))}
        </div>
      )}
    </>
  );
}

// --------------------------------------------------------------------------- //
//  Transcript pane                                                            //
// --------------------------------------------------------------------------- //

function TranscriptPane({
  transcript,
  activeSegmentIdx,
  speakerNames,
  speakerOrder,
  segmentCountByLabel,
  seek,
  transcriptRef,
  transcribeProvider,
  onReassignSegment,
  onRenameSpeaker,
  onMintSpeakerLabel,
}: {
  transcript: VideoTranscriptSegment[] | null;
  activeSegmentIdx: number;
  speakerNames: Map<string, string>;
  speakerOrder: string[];
  segmentCountByLabel: Map<string, number>;
  seek: (s: number) => void;
  transcriptRef: React.RefObject<HTMLDivElement>;
  transcribeProvider: string | null;
  onReassignSegment: (segmentId: number, newLabel: string) => Promise<void>;
  onRenameSpeaker: (label: string, name: string | null) => Promise<void>;
  onMintSpeakerLabel: () => string;
}) {
  // Which segment's speaker picker is open. Single source of truth so
  // opening one closes any other; null means no picker is open.
  const [openSegId, setOpenSegId] = useState<number | null>(null);

  // Surface which backend produced this transcript so users know whether
  // they're looking at real acoustic diarization (Deepgram) or the
  // text-only LLM fallback (OpenAI Whisper + Claude speaker assignment).
  const providerNote =
    transcribeProvider === "deepgram"
      ? "Transcribed by Deepgram Nova-3 with acoustic speaker diarization."
      : transcribeProvider === "openai"
        ? "Transcribed by OpenAI Whisper; speaker labels are approximate (text-only)."
        : "Speaker labels are approximate.";

  return (
    <>
      <div className="video-player__transcript-head">
        <span className="muted video-player__transcript-note">
          {providerNote} Click any speaker to rename or reassign.
        </span>
      </div>
      {transcript === null ? (
        <div className="muted">Loading transcript…</div>
      ) : transcript.length === 0 ? (
        <div className="muted">No transcript available for this video.</div>
      ) : (
        <div className="video-player__transcript" ref={transcriptRef}>
          {transcript.map((seg, i) => {
            const active = i === activeSegmentIdx;
            const currentLabel = seg.speaker_label ?? "";
            const speakerText = speakerNames.get(currentLabel) ?? "";
            const isPickerOpen = openSegId === seg.id;
            return (
              <div
                key={seg.id}
                data-seg-idx={i}
                className={`video-player__seg${active ? " video-player__seg--active" : ""}`}
                onClick={(ev) => {
                  // Don't seek when the click came from inside the
                  // speaker picker; React's synthetic events bubble.
                  if ((ev.target as HTMLElement).closest(".video-player__spk")) return;
                  seek(seg.start_seconds);
                }}
                role="button"
                tabIndex={0}
                onKeyDown={(ev) => {
                  if (ev.key === "Enter" || ev.key === " ") {
                    ev.preventDefault();
                    seek(seg.start_seconds);
                  }
                }}
                aria-current={active ? "true" : undefined}
              >
                <div className="video-player__seg-meta">
                  <span className="video-player__seg-time">
                    {formatClock(seg.start_seconds)}
                  </span>
                  {speakerText && (
                    <SpeakerChip
                      label={speakerText}
                      currentLabel={currentLabel}
                      isOpen={isPickerOpen}
                      onToggle={() =>
                        setOpenSegId(isPickerOpen ? null : seg.id)
                      }
                      onClose={() => setOpenSegId(null)}
                      speakerOrder={speakerOrder}
                      speakerNames={speakerNames}
                      segmentCountByLabel={segmentCountByLabel}
                      onPick={async (newLabel) => {
                        setOpenSegId(null);
                        if (newLabel !== currentLabel) {
                          await onReassignSegment(seg.id, newLabel);
                        }
                      }}
                      onRename={async (label, name) => {
                        await onRenameSpeaker(label, name);
                      }}
                      onAddSpeaker={async () => {
                        const fresh = onMintSpeakerLabel();
                        setOpenSegId(null);
                        await onReassignSegment(seg.id, fresh);
                      }}
                    />
                  )}
                </div>
                <div className="video-player__seg-text">{seg.text}</div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}

// --------------------------------------------------------------------------- //
//  Speaker chip + picker popover                                              //
// --------------------------------------------------------------------------- //

function SpeakerChip({
  label,
  currentLabel,
  isOpen,
  onToggle,
  onClose,
  speakerOrder,
  speakerNames,
  segmentCountByLabel,
  onPick,
  onRename,
  onAddSpeaker,
}: {
  /** Human-friendly text shown on the chip (the user's display name
   *  or "Speaker N" fallback). */
  label: string;
  /** Raw spk_<n> token for the segment this chip belongs to. */
  currentLabel: string;
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
  speakerOrder: string[];
  speakerNames: Map<string, string>;
  segmentCountByLabel: Map<string, number>;
  onPick: (newLabel: string) => Promise<void>;
  onRename: (label: string, name: string | null) => Promise<void>;
  onAddSpeaker: () => Promise<void>;
}) {
  const popoverRef = useRef<HTMLDivElement>(null);
  const [renameDraft, setRenameDraft] = useState<string>("");
  const [renameTarget, setRenameTarget] = useState<string | null>(null);

  // Click-outside + Escape to close.
  useEffect(() => {
    if (!isOpen) return;
    const onDocClick = (ev: MouseEvent) => {
      if (!popoverRef.current) return;
      if (!popoverRef.current.contains(ev.target as Node)) {
        onClose();
      }
    };
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === "Escape") onClose();
    };
    // Defer one frame so the click that opened us doesn't also close us.
    const t = setTimeout(() => {
      document.addEventListener("mousedown", onDocClick);
      document.addEventListener("keydown", onKey);
    }, 0);
    return () => {
      clearTimeout(t);
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [isOpen, onClose]);

  // Reset the rename editor whenever we open/close.
  useEffect(() => {
    if (!isOpen) {
      setRenameTarget(null);
      setRenameDraft("");
    }
  }, [isOpen]);

  const beginRename = (label: string) => {
    setRenameTarget(label);
    // Pre-fill with the current name if it's a custom one (not the
    // synthesized "Speaker N" placeholder); otherwise start empty so
    // typing replaces the default cleanly.
    const current = speakerNames.get(label) ?? "";
    const looksLikeDefault = /^Speaker \d+$/.test(current);
    setRenameDraft(looksLikeDefault ? "" : current);
  };

  const commitRename = async () => {
    if (!renameTarget) return;
    await onRename(renameTarget, renameDraft.trim() || null);
    setRenameTarget(null);
    setRenameDraft("");
  };

  return (
    <span className="video-player__spk">
      <button
        type="button"
        className={`video-player__seg-speaker${isOpen ? " video-player__seg-speaker--open" : ""}`}
        onClick={(ev) => {
          ev.stopPropagation();
          onToggle();
        }}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        title="Click to rename or reassign speaker"
      >
        {label}
      </button>
      {isOpen && (
        <div
          className="video-player__spk-pop"
          ref={popoverRef}
          role="menu"
          onClick={(ev) => ev.stopPropagation()}
        >
          <div className="video-player__spk-pop-head">Speaker for this line</div>
          <ul className="video-player__spk-pop-list">
            {speakerOrder.map((lbl) => {
              const name = speakerNames.get(lbl) ?? lbl;
              const count = segmentCountByLabel.get(lbl) ?? 0;
              const isCurrent = lbl === currentLabel;
              const isRenaming = renameTarget === lbl;
              return (
                <li key={lbl} className="video-player__spk-pop-item">
                  {isRenaming ? (
                    <form
                      className="video-player__spk-rename"
                      onSubmit={(ev) => {
                        ev.preventDefault();
                        void commitRename();
                      }}
                    >
                      <input
                        autoFocus
                        type="text"
                        value={renameDraft}
                        placeholder="Name (e.g. John Smith)"
                        onChange={(ev) => setRenameDraft(ev.target.value)}
                        onKeyDown={(ev) => {
                          if (ev.key === "Escape") {
                            ev.preventDefault();
                            setRenameTarget(null);
                          }
                        }}
                      />
                      <button type="submit" className="btn btn--xs">
                        Save
                      </button>
                      <button
                        type="button"
                        className="btn btn--xs btn--ghost"
                        onClick={() => setRenameTarget(null)}
                      >
                        Cancel
                      </button>
                    </form>
                  ) : (
                    <button
                      type="button"
                      className={`video-player__spk-pop-pick${isCurrent ? " is-current" : ""}`}
                      onClick={() => void onPick(lbl)}
                    >
                      <span className="video-player__spk-pop-name">
                        {isCurrent && (
                          <span
                            className="video-player__spk-pop-dot"
                            aria-hidden="true"
                          />
                        )}
                        {name}
                      </span>
                      <span className="video-player__spk-pop-count muted">
                        {count} {count === 1 ? "line" : "lines"}
                      </span>
                      <span
                        role="button"
                        tabIndex={0}
                        className="video-player__spk-pop-edit"
                        title="Rename this speaker"
                        onClick={(ev) => {
                          ev.stopPropagation();
                          beginRename(lbl);
                        }}
                        onKeyDown={(ev) => {
                          if (ev.key === "Enter" || ev.key === " ") {
                            ev.preventDefault();
                            beginRename(lbl);
                          }
                        }}
                      >
                        Rename
                      </span>
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
          <div className="video-player__spk-pop-foot">
            <button
              type="button"
              className="btn btn--xs btn--ghost"
              onClick={() => void onAddSpeaker()}
            >
              + New speaker for this line
            </button>
          </div>
        </div>
      )}
    </span>
  );
}

// --------------------------------------------------------------------------- //
//  Timeline pane                                                              //
// --------------------------------------------------------------------------- //

const CATEGORY_LABELS: Record<VideoTimelineCategory, string> = {
  admission: "Admission",
  statement: "Statement",
  identification: "Identification",
  action_item: "Action Item",
  contradiction: "Contradiction",
  evidence: "Evidence",
  visual: "Visual",
  other: "Other",
};

function TimelinePane({
  timeline,
  currentTime,
  seek,
  onRegenerate,
  regenerating,
  videoReady,
  hasGroundableData,
  onToggleFavorite,
}: {
  timeline: VideoTimeline | null;
  currentTime: number;
  seek: (s: number) => void;
  onRegenerate: () => void;
  regenerating: boolean;
  videoReady: boolean;
  hasGroundableData: boolean;
  onToggleFavorite: (eventId: number) => Promise<void>;
}) {
  const status = timeline?.status ?? "idle";
  const isGenerating = status === "queued" || status === "running";

  // Highlight the event closest-to-but-not-after currentTime, the same
  // way the transcript highlights its active segment.
  const activeIdx = useMemo(() => {
    if (!timeline || timeline.items.length === 0) return -1;
    let lo = 0;
    let hi = timeline.items.length - 1;
    let best = -1;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      if (timeline.items[mid].at_seconds <= currentTime) {
        best = mid;
        lo = mid + 1;
      } else {
        hi = mid - 1;
      }
    }
    return best;
  }, [timeline, currentTime]);

  return (
    <>
      <div className="video-player__timeline-head">
        <span className="muted video-player__transcript-note">
          AI-extracted from the transcript and scenes. Click a row to jump.
        </span>
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={onRegenerate}
          disabled={!videoReady || regenerating || isGenerating || !hasGroundableData}
          title={
            !hasGroundableData
              ? "Need at least one scene or transcript segment"
              : !videoReady
                ? "Wait for video to finish processing"
                : isGenerating
                  ? "Already generating…"
                  : "Re-run extraction"
          }
        >
          {isGenerating ? "Generating…" : "Regenerate"}
        </button>
      </div>

      {!timeline ? (
        <div className="muted">Loading timeline…</div>
      ) : status === "failed" ? (
        <div className="alert alert--error">
          Timeline generation failed.
          {timeline.error && (
            <div className="muted" style={{ marginTop: 6 }}>
              {timeline.error}
            </div>
          )}
        </div>
      ) : isGenerating ? (
        <div className="video-player__timeline-banner">
          <span className="video-player__spinner" aria-hidden />
          Generating timeline events—
        </div>
      ) : timeline.items.length === 0 ? (
        <div className="muted">
          {hasGroundableData
            ? "No notable events were identified for this video."
            : "Need at least one scene or transcript segment to build a timeline."}
        </div>
      ) : (
        <div className="video-player__timeline">
          {timeline.items.map((ev, i) => (
            <TimelineRow
              key={ev.id}
              event={ev}
              active={i === activeIdx}
              onPick={() => seek(ev.at_seconds)}
              onToggleFavorite={() => onToggleFavorite(ev.id)}
            />
          ))}
        </div>
      )}
    </>
  );
}

function TimelineRow({
  event,
  active,
  onPick,
  onToggleFavorite,
}: {
  event: VideoTimelineEvent;
  active: boolean;
  onPick: () => void;
  onToggleFavorite?: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const hasDetail = !!(event.source_quote || event.description);
  return (
    <div
      className={`video-player__tl-row${active ? " video-player__tl-row--active" : ""}`}
      data-category={event.category}
    >
      {/* Top bar: time · badge · star · optional expand chevron */}
      <div className="video-player__tl-meta">
        <span className="video-player__tl-time">
          {formatClock(event.at_seconds)}
        </span>
        <span
          className={`video-player__tl-cat video-player__tl-cat--${event.category}`}
        >
          {CATEGORY_LABELS[event.category] ?? "Event"}
        </span>
        <span className="video-player__tl-meta-spacer" />
        {onToggleFavorite && (
          <button
            type="button"
            className={`video-player__tl-fav${event.is_favorite ? " video-player__tl-fav--on" : ""}`}
            onClick={(ev) => { ev.stopPropagation(); onToggleFavorite(); }}
            aria-pressed={!!event.is_favorite}
            title={event.is_favorite ? "Remove from favorites" : "Add to favorites"}
            aria-label={event.is_favorite ? "Remove from favorites" : "Add to favorites"}
          >
            <StarIcon filled={!!event.is_favorite} />
          </button>
        )}
        {hasDetail && (
          <button
            type="button"
            className="video-player__tl-expand"
            onClick={(ev) => { ev.stopPropagation(); setExpanded((v) => !v); }}
            aria-expanded={expanded}
            title={expanded ? "Collapse" : "Show source quote"}
          >
            {expanded ? "▲" : "▼"}
          </button>
        )}
      </div>

      {/* Label: full-width clickable, text wraps naturally */}
      <button
        type="button"
        className="video-player__tl-label"
        onClick={onPick}
        title="Jump to this moment"
      >
        {event.label}
      </button>

      {/* Expandable: description + source quote */}
      {expanded && (
        <div className="video-player__tl-detail">
          {event.description && (
            <p className="video-player__tl-desc">{event.description}</p>
          )}
          {event.source_quote && (
            <blockquote className="video-player__tl-source-quote">
              "{event.source_quote}"
            </blockquote>
          )}
        </div>
      )}
    </div>
  );
}


// --------------------------------------------------------------------------- //
//  Scene strip                                                                //
// --------------------------------------------------------------------------- //

const SCENES_PAGE_SIZE = 12;

function SceneStrip({
  scenes,
  activeIdx,
  onPick,
}: {
  scenes: VideoScene[] | null;
  activeIdx: number;
  onPick: (scene: VideoScene) => void;
}) {
  // Pagination: scene lists can run into the hundreds on long videos
  // (e.g. evidence montages, body-worn cam exports). A horizontal
  // overflow strip stops scaling around ~20 tiles before it gets
  // unusable on a normal display. So we lay out tiles in a wrapping
  // grid, show SCENES_PAGE_SIZE per page, and auto-follow the playing
  // scene so the user always sees "where they are" without having to
  // page manually.
  const [page, setPage] = useState(0);
  const [jumpDraft, setJumpDraft] = useState("");

  const total = scenes?.length ?? 0;
  const numPages = Math.max(1, Math.ceil(total / SCENES_PAGE_SIZE));
  const activePage =
    activeIdx >= 0 && activeIdx < total
      ? Math.floor(activeIdx / SCENES_PAGE_SIZE)
      : -1;

  // Follow the playing scene. We intentionally don't track "did the
  // user manually page?" -- once playback advances to a new scene
  // the strip re-syncs. Short manual scouting still works; if the
  // user picks a tile, that seeks the video, which sets activeIdx
  // onto the same page, so the auto-sync becomes a no-op.
  useEffect(() => {
    if (activePage >= 0) setPage(activePage);
  }, [activePage]);

  // Clamp on scenes-list changes (reprocess, regenerate, etc.).
  useEffect(() => {
    if (page >= numPages) setPage(Math.max(0, numPages - 1));
  }, [page, numPages]);
  // Keyframes are served by the API via a redirect to S3, and authed by
  // a short-lived signed token already baked into `keyframe_url` (the
  // engine signs it server-side at list time). We can therefore use a
  // plain `<img src>` and let the browser's standard image loader
  // follow the redirect — fetch()+Blob URLs would force CORS on S3
  // which we deliberately don't enable.
  if (scenes === null) {
    return <div className="video-player__strip muted">Loading scenes…</div>;
  }
  if (scenes.length === 0) {
    return (
      <div className="video-player__strip muted">
        No scenes detected — the video may be a single continuous shot.
      </div>
    );
  }

  const BASE = (import.meta as any).env?.VITE_API_BASE ?? "/api";
  const start = page * SCENES_PAGE_SIZE;
  const visible = scenes.slice(start, start + SCENES_PAGE_SIZE);
  const showPager = numPages > 1;

  const submitJump = (ev: React.FormEvent) => {
    ev.preventDefault();
    const raw = jumpDraft.trim();
    if (!raw) return;
    const n = parseInt(raw, 10);
    if (!Number.isFinite(n) || n < 1 || n > total) {
      setJumpDraft("");
      return;
    }
    // Treat the input as a 1-based scene number; auto-seek the video
    // to that scene rather than just paging to it. Feels nicer for the
    // "find scene 47" use case the pager exists for.
    const target = scenes[n - 1];
    setPage(Math.floor((n - 1) / SCENES_PAGE_SIZE));
    setJumpDraft("");
    if (target) onPick(target);
  };

  return (
    <div className="video-player__strip">
      <div className="video-player__strip-head">
        <h3>
          Scenes <span className="muted">({total})</span>
        </h3>
        <span className="muted">
          Click a tile to jump the player to that point.
        </span>
      </div>
      <div className="video-player__strip-grid">
        {visible.map((s, localIdx) => {
          const i = start + localIdx;
          // keyframe_url is /api/cases/.../keyframes/N?token=...; reroute it
          // through whatever VITE_API_BASE the SPA was built with, matching
          // how playSrc is constructed for /raw above.
          const url = s.keyframe_url
            ? `${BASE}${s.keyframe_url.replace(/^\/api/, "")}`
            : null;
          const active = i === activeIdx;
          return (
            <button
              key={s.id}
              type="button"
              className={`video-player__tile${active ? " video-player__tile--active" : ""}`}
              onClick={() => onPick(s)}
              title={s.ai_summary ?? `Scene ${s.scene_index + 1}`}
            >
              <div className="video-player__tile-frame">
                {url ? (
                  <img
                    src={url}
                    alt={s.ai_summary ?? `Scene ${s.scene_index + 1}`}
                    loading="lazy"
                  />
                ) : (
                  <div className="video-player__tile-empty">—</div>
                )}
              </div>
              <div className="video-player__tile-meta">
                <span className="video-player__tile-time">
                  {formatClock(s.start_seconds)}
                </span>
                <span className="video-player__tile-idx">
                  #{s.scene_index + 1}
                </span>
              </div>
              {s.ai_summary && (
                <div className="video-player__tile-summary">{s.ai_summary}</div>
              )}
            </button>
          );
        })}
      </div>
      {showPager && (
        <div className="video-player__strip-pager">
          <button
            type="button"
            className="btn btn--xs btn--ghost"
            disabled={page === 0}
            onClick={() => setPage(0)}
            title="First page"
            aria-label="First page"
          >
            {"<<"}
          </button>
          <button
            type="button"
            className="btn btn--xs btn--ghost"
            disabled={page === 0}
            onClick={() => setPage(page - 1)}
          >
            {"< Prev"}
          </button>
          <span className="muted video-player__strip-pager-status">
            {`Scenes ${start + 1}${visible.length > 1 ? `-${start + visible.length}` : ""} of ${total} `}
            <span className="video-player__strip-pager-page">
              (Page {page + 1} / {numPages})
            </span>
          </span>
          <button
            type="button"
            className="btn btn--xs btn--ghost"
            disabled={page >= numPages - 1}
            onClick={() => setPage(page + 1)}
          >
            {"Next >"}
          </button>
          <button
            type="button"
            className="btn btn--xs btn--ghost"
            disabled={page >= numPages - 1}
            onClick={() => setPage(numPages - 1)}
            title="Last page"
            aria-label="Last page"
          >
            {">>"}
          </button>
          {numPages > 2 && (
            <form
              className="video-player__strip-pager-jump"
              onSubmit={submitJump}
            >
              <label className="muted" htmlFor="scene-jump">
                Jump to #
              </label>
              <input
                id="scene-jump"
                type="number"
                min={1}
                max={total}
                value={jumpDraft}
                onChange={(ev) => setJumpDraft(ev.target.value)}
                placeholder={String(total)}
              />
              <button type="submit" className="btn btn--xs">
                Go
              </button>
            </form>
          )}
          {activePage >= 0 && activePage !== page && (
            <button
              type="button"
              className="btn btn--xs"
              onClick={() => setPage(activePage)}
              title="Jump to the page containing the currently-playing scene"
            >
              {`Show current (#${activeIdx + 1})`}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// --------------------------------------------------------------------------- //
//  Helpers                                                                    //
// --------------------------------------------------------------------------- //

/** Binary search over a sorted list of spans for the one containing `t`.
 *  Returns -1 when no span matches. */
function findSpan<T>(
  list: T[],
  t: number,
  start: (x: T) => number,
  end: (x: T) => number,
): number {
  let lo = 0;
  let hi = list.length - 1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    const s = start(list[mid]);
    const e = end(list[mid]);
    if (t < s) hi = mid - 1;
    else if (t >= e) lo = mid + 1;
    else return mid;
  }
  // If we fell off the end of the last span (currentTime === duration),
  // still treat the final span as active so the UI doesn't flicker off.
  if (list.length > 0 && t >= end(list[list.length - 1]) - 0.001) {
    return list.length - 1;
  }
  return -1;
}

/** Map raw spk_N tokens to a human label per video.
 *
 * Priority:
 *   1. User-supplied display_name (from video_speakers, via the segment's
 *      speaker_display_name pull-through or the speakers list).
 *   2. "Speaker 1 / Speaker 2 / ..." numbered in appearance order, so a
 *      diarizer that produced spk_0 + spk_2 (skipping spk_1) still
 *      renders as "Speaker 1" + "Speaker 2" -- the integer in the
 *      token is an internal id, not what we show.
 */
function resolveSpeakerNames(
  transcript: VideoTranscriptSegment[],
  speakers: VideoSpeaker[],
): Map<string, string> {
  const order: string[] = [];
  const custom = new Map<string, string>();
  for (const seg of transcript) {
    const raw = seg.speaker_label ?? "";
    if (!raw) continue;
    if (!order.includes(raw)) order.push(raw);
    if (seg.speaker_display_name) custom.set(raw, seg.speaker_display_name);
  }
  for (const sp of speakers) {
    if (!order.includes(sp.speaker_label)) order.push(sp.speaker_label);
    if (sp.display_name) custom.set(sp.speaker_label, sp.display_name);
  }
  const out = new Map<string, string>();
  order.forEach((label, i) => {
    out.set(label, custom.get(label) ?? `Speaker ${i + 1}`);
  });
  return out;
}

function formatClock(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds <= 0) return "0:00";
  const s = Math.floor(seconds);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
  return `${m}:${String(sec).padStart(2, "0")}`;
}

function formatDuration(seconds?: number | null): string {
  if (!seconds && seconds !== 0) return "—";
  return formatClock(Number(seconds));
}
